import { useState, useEffect } from 'react'
import { moment, getSlotHeight } from '../utils/dateUtils'

export default function useWorklogDragAndDrop(onWorklogUpdate, styles, callbacks = {}, currentUser = null, viewDensity = 'relaxed', userTimezone = 'UTC') {
    // States for new worklog creation
    const [dragStart, setDragStart] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [dragPreview, setDragPreview] = useState(null)
    
    // States for existing worklog dragging
    const [draggedWorklog, setDraggedWorklog] = useState(null)
    const [isDraggingWorklog, setIsDraggingWorklog] = useState(false)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [mouseDownWorklog, setMouseDownWorklog] = useState(null)
    const [mouseDownPosition, setMouseDownPosition] = useState({ x: 0, y: 0 })
    
    // State for modifier key tracking
    const [isAltPressed, setIsAltPressed] = useState(false)
    const [isShiftPressed, setIsShiftPressed] = useState(false)
    
    // States for worklog resizing
    const [isResizing, setIsResizing] = useState(false)
    const [resizeWorklog, setResizeWorklog] = useState(null)
    const [resizeType, setResizeType] = useState(null) // 'top' or 'bottom'
    const [resizeStartData, setResizeStartData] = useState(null)

    // Global mouse and keyboard event handlers
    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            // Track modifier key states during drag
            setIsAltPressed(e.altKey)
            setIsShiftPressed(e.shiftKey)
            
            // Check if we should start dragging an existing worklog
            if (mouseDownWorklog && !isDraggingWorklog) {
                const deltaX = Math.abs(e.clientX - mouseDownPosition.x)
                const deltaY = Math.abs(e.clientY - mouseDownPosition.y)
                const dragThreshold = 5 // pixels
                
                if (deltaX > dragThreshold || deltaY > dragThreshold) {
                    // Start dragging after threshold is exceeded
                    setDraggedWorklog({ ...mouseDownWorklog })
                    setIsDraggingWorklog(true)
                    setMouseDownWorklog(null)
                }
                return
            }
            
            // Handle new worklog creation drag
            if (isDragging && dragStart && !isDraggingWorklog) {
                handleNewWorklogDrag(e)
                return
            }
            
            // Handle existing worklog drag
            if (isDraggingWorklog && draggedWorklog) {
                handleExistingWorklogDrag(e)
                return
            }
            
            // Handle worklog resize
            if (isResizing && resizeWorklog) {
                handleWorklogResize(e)
                return
            }
        }
        
        const handleNewWorklogDrag = (e) => {
            // Find the time slot under the mouse (worklogs have pointer-events disabled during drag)
            const element = document.elementFromPoint(e.clientX, e.clientY)
            if (!element || !element.classList.contains(styles.timeSlot)) return

            const slotData = element.getAttribute('data-slot')
            const dateData = element.getAttribute('data-date')
            if (!slotData || !dateData) return

            const [hour, minute] = slotData.split(':').map(Number)
            const date = new Date(dateData)

            const currentSlotTime = new Date(date)
            currentSlotTime.setHours(hour, minute, 0, 0)

            let startTime = dragStart.startTime
            let endTime = new Date(currentSlotTime.getTime() + 15 * 60 * 1000) // End of current slot

            // Determine if dragging up or down
            if (currentSlotTime < dragStart.startTime) {
                // Dragging up - adjust start time, keep original end time
                startTime = currentSlotTime
                endTime = new Date(dragStart.startTime.getTime() + 15 * 60 * 1000)
            } else {
                // Dragging down - keep original start time, adjust end time
                startTime = dragStart.startTime
                endTime = new Date(currentSlotTime.getTime() + 15 * 60 * 1000)
            }

            // Ensure minimum 15-minute duration
            if (endTime <= startTime) {
                endTime = new Date(startTime.getTime() + 15 * 60 * 1000)
            }

            setDragPreview({
                startTime,
                endTime,
                date: dragStart.date
            })
        }

        const handleGlobalMouseUp = (e) => {
            // Reset mouse down state if no drag started
            if (mouseDownWorklog && !isDraggingWorklog) {
                setMouseDownWorklog(null)
                setMouseDownPosition({ x: 0, y: 0 })
                return
            }
            
            // Handle new worklog creation
            if (isDragging && dragStart && dragPreview && !isDraggingWorklog) {
                handleNewWorklogDrop(e)
                return
            }
            
            // Handle existing worklog drag
            if (isDraggingWorklog && draggedWorklog) {
                handleExistingWorklogDrop(e)
                return
            }
            
            // Handle worklog resize
            if (isResizing && resizeWorklog) {
                handleWorklogResizeDrop(e)
                return
            }
        }
        
        const handleNewWorklogDrop = (e) => {
            // Prepare worklog data for modal
            const worklogData = {
                title: '',
                startTime: dragPreview.startTime,
                endTime: dragPreview.endTime,
                date: dragStart.date,
                type: 'work'
            }
            
            // Reset drag state
            setDragStart(null)
            setIsDragging(false)
            setDragPreview(null)
            
            // Callback to parent for modal handling
            if (callbacks.onNewWorklogDrop) {
                callbacks.onNewWorklogDrop(worklogData)
            }
        }
        
        const handleWorklogResize = (e) => {
            // Find the time slot under the mouse
            const element = document.elementFromPoint(e.clientX, e.clientY)
            if (!element || !element.classList.contains(styles.timeSlot)) return
            
            const slotData = element.getAttribute('data-slot')
            const dateData = element.getAttribute('data-date')
            if (!slotData || !dateData) return
            
            const [hour, minute] = slotData.split(':').map(Number)
            const date = new Date(dateData)
            
            const newTime = new Date(date)
            newTime.setHours(hour, minute, 0, 0)
            
            let newStartTime = resizeWorklog.startTime
            let newEndTime = resizeWorklog.endTime
            
            if (resizeType === 'top') {
                // Resizing from top - change start time
                newStartTime = newTime
                // Ensure minimum 15-minute duration
                if (newStartTime >= newEndTime) {
                    newStartTime = new Date(newEndTime.getTime() - 15 * 60 * 1000)
                }
            } else if (resizeType === 'bottom') {
                // Resizing from bottom - change end time  
                newEndTime = new Date(newTime.getTime() + 15 * 60 * 1000) // End of current slot
                // Ensure minimum 15-minute duration
                if (newEndTime <= newStartTime) {
                    newEndTime = new Date(newStartTime.getTime() + 15 * 60 * 1000)
                }
            }
            
            // Update the resize worklog with new times
            setResizeWorklog({
                ...resizeWorklog,
                startTime: newStartTime,
                endTime: newEndTime
            })
        }
        
        const handleExistingWorklogDrag = (e) => {
            // Find the time slot under the mouse
            const element = document.elementFromPoint(e.clientX, e.clientY)
            if (!element || !element.classList.contains(styles.timeSlot)) return
            
            const slotData = element.getAttribute('data-slot')
            const dateData = element.getAttribute('data-date')
            if (!slotData || !dateData) return
            
            const [hour, minute] = slotData.split(':').map(Number)
            const date = new Date(dateData)
            
            const newStartTime = new Date(date)
            newStartTime.setHours(hour, minute, 0, 0)
            
            // Calculate duration and new end time
            const originalDuration = draggedWorklog.endTime.getTime() - draggedWorklog.startTime.getTime()
            const newEndTime = new Date(newStartTime.getTime() + originalDuration)
            
            // Update the dragged worklog with new times
            setDraggedWorklog({
                ...draggedWorklog,
                startTime: newStartTime,
                endTime: newEndTime,
                date
            })
        }
        
        const handleWorklogResizeDrop = async (e) => {
            if (!resizeWorklog) return

            // Signal start of worklog resize operation
            if (callbacks.onWorklogMoveStart) {
                callbacks.onWorklogMoveStart()
            }

            try {
                const updateData = {
                    start_time: resizeWorklog.startTime.toISOString(),
                    end_time: resizeWorklog.endTime.toISOString(),
                    description: resizeWorklog.title,
                    project: resizeWorklog.project?.id || resizeWorklog.project,
                    task: resizeWorklog.task?.id || resizeWorklog.task,
                    user_id: resizeWorklog.userId,
                    billable: resizeWorklog.billable
                }

                // Call update API
                await onWorklogUpdate(resizeWorklog.id, updateData)
            } catch (error) {
                console.error('Failed to resize worklog:', error)
                // Revert the worklog to original state on error
                // The parent component will handle showing the error
            } finally {
                // Signal end of worklog resize operation
                if (callbacks.onWorklogMoveEnd) {
                    callbacks.onWorklogMoveEnd()
                }
            }

            // Reset resize state
            setIsResizing(false)
            setResizeWorklog(null)
            setResizeType(null)
            setResizeStartData(null)
        }
        
        const handleExistingWorklogDrop = async (e) => {
            if (!draggedWorklog) return

            // Signal start of worklog operation
            if (callbacks.onWorklogMoveStart) {
                callbacks.onWorklogMoveStart()
            }

            try {
                if (isAltPressed) {
                    // Alt key pressed - duplicate the worklog
                    // If Shift is also pressed, assign to current user; otherwise keep original user
                    console.log('Drag & Drop Debug:', {
                        isAltPressed,
                        isShiftPressed,
                        currentUserId: currentUser?.id,
                        originalUserId: draggedWorklog.userId
                    })
                    const assignToCurrentUser = isShiftPressed && currentUser?.id
                    console.log('assignToCurrentUser:', assignToCurrentUser)
                    const duplicateData = {
                        title: draggedWorklog.title,
                        startTime: draggedWorklog.startTime,
                        endTime: draggedWorklog.endTime,
                        date: draggedWorklog.date,
                        project: draggedWorklog.project?.id || draggedWorklog.project,
                        task: draggedWorklog.task?.id || draggedWorklog.task,
                        userId: assignToCurrentUser ? currentUser.id : draggedWorklog.userId,
                        billable: draggedWorklog.billable,
                        type: 'work'
                    }

                    console.log('Final duplicateData.userId:', duplicateData.userId)

                    // Call create callback to duplicate the worklog
                    if (callbacks.onWorklogDuplicate) {
                        await callbacks.onWorklogDuplicate(duplicateData)
                    }
                } else {
                    // Normal drag - move the worklog
                    const updateData = {
                        start_time: draggedWorklog.startTime.toISOString(),
                        end_time: draggedWorklog.endTime.toISOString(),
                        description: draggedWorklog.title,
                        project: draggedWorklog.project?.id || draggedWorklog.project,
                        task: draggedWorklog.task?.id || draggedWorklog.task,
                        user_id: draggedWorklog.userId,
                        billable: draggedWorklog.billable
                    }

                    console.log('Drag & Drop - Updating worklog:', draggedWorklog.id, 'with data:', updateData)
                    console.log('Original user_id:', draggedWorklog.userId, 'Sending user_id:', updateData.user_id)

                    // Call update API
                    await onWorklogUpdate(draggedWorklog.id, updateData)
                }
            } catch (error) {
                console.error('Failed to update/duplicate worklog:', error)
                // Revert the worklog to original state on error
                // The parent component will handle showing the error
            } finally {
                // Signal end of worklog operation
                if (callbacks.onWorklogMoveEnd) {
                    callbacks.onWorklogMoveEnd()
                }
            }
            
            // Reset drag state
            setDraggedWorklog(null)
            setIsDraggingWorklog(false)
            setIsAltPressed(false)
            setIsShiftPressed(false)
        }
        
        const handleKeyDown = (e) => {
            if (e.key === 'Alt') {
                setIsAltPressed(true)
            } else if (e.key === 'Shift') {
                setIsShiftPressed(true)
            } else if (e.key === 'Escape') {
                // Cancel any active drag or resize operation
                if (isDragging || isDraggingWorklog || mouseDownWorklog || isResizing) {
                    e.preventDefault()
                    setDragStart(null)
                    setIsDragging(false)
                    setDragPreview(null)
                    setDraggedWorklog(null)
                    setIsDraggingWorklog(false)
                    setMouseDownWorklog(null)
                    setMouseDownPosition({ x: 0, y: 0 })
                    setDragOffset({ x: 0, y: 0 })
                    setIsAltPressed(false)
                    setIsShiftPressed(false)
                    // Reset resize states
                    setIsResizing(false)
                    setResizeWorklog(null)
                    setResizeType(null)
                    setResizeStartData(null)
                }
            }
        }
        
        const handleKeyUp = (e) => {
            if (e.key === 'Alt') {
                setIsAltPressed(false)
            } else if (e.key === 'Shift') {
                setIsShiftPressed(false)
            }
        }
        
        // Always listen for keyboard events
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('keyup', handleKeyUp)
        
        if (isDragging || isDraggingWorklog || mouseDownWorklog || isResizing) {
            document.addEventListener('mousemove', handleGlobalMouseMove)
            document.addEventListener('mouseup', handleGlobalMouseUp)
            
            // Disable pointer events on all worklogs during drag/resize to allow dropping over them
            if (isDragging || isDraggingWorklog || isResizing) {
                const worklogs = document.querySelectorAll(`.${styles.worklog}`)
                worklogs.forEach(worklog => {
                    worklog.style.pointerEvents = 'none'
                })
            }
        }
        
        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove)
            document.removeEventListener('mouseup', handleGlobalMouseUp)
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('keyup', handleKeyUp)
            
            // Restore pointer events on all worklogs
            const worklogs = document.querySelectorAll(`.${styles.worklog}`)
            worklogs.forEach(worklog => {
                worklog.style.pointerEvents = ''
            })
        }
    }, [isDragging, dragStart, dragPreview, isDraggingWorklog, draggedWorklog, mouseDownWorklog, mouseDownPosition, isResizing, resizeWorklog, resizeType, onWorklogUpdate, styles])

    // Handler for starting new worklog drag
    const handleMouseDown = (e, date, timeSlot) => {
        e.preventDefault()
        const startTime = new Date(date)
        startTime.setHours(timeSlot.hour, timeSlot.minute, 0, 0)

        setDragStart({ date, timeSlot, startTime })
        setIsDragging(true)
        setDragPreview({
            startTime,
            endTime: new Date(startTime.getTime() + 15 * 60 * 1000), // 15 minutes default
            date
        })
    }

    // Handler for starting existing worklog drag
    const handleWorklogMouseDown = (e, worklog) => {
        // Store mouse down state but don't start dragging immediately
        setMouseDownWorklog(worklog)
        setMouseDownPosition({ x: e.clientX, y: e.clientY })
        
        // Store the drag offset for when dragging actually starts
        const rect = e.currentTarget.getBoundingClientRect()
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        })
    }

    // Handler for double click to create worklog
    const handleDoubleClick = (e, date, timeSlot) => {
        e.preventDefault()
        const startTime = new Date(date)
        startTime.setHours(timeSlot.hour, timeSlot.minute, 0, 0)
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000) // 1 hour default
        
        const worklogData = {
            title: '',
            startTime,
            endTime,
            date,
            type: 'work'
        }
        
        // Callback to parent for modal handling
        if (callbacks.onDoubleClick) {
            callbacks.onDoubleClick(worklogData)
        }
    }

    // Style calculators
    const getDragPreviewStyle = (dragPreview) => {
        if (!dragPreview) return {}

        // Extract hour/minute in user's timezone for correct grid positioning
        const startMinutes = moment.tz(dragPreview.startTime, userTimezone).hour() * 60 + moment.tz(dragPreview.startTime, userTimezone).minute()
        const endMinutes = moment.tz(dragPreview.endTime, userTimezone).hour() * 60 + moment.tz(dragPreview.endTime, userTimezone).minute()
        const duration = endMinutes - startMinutes

        const slotHeight = getSlotHeight(viewDensity)
        const top = (startMinutes / 15) * slotHeight
        const height = (duration / 15) * slotHeight

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: '4px',
            right: '4px'
        }
    }
    
    const getDragPreviewStyleForExistingWorklog = (draggedWorklog) => {
        if (!draggedWorklog) return {}

        // Extract hour/minute in user's timezone for correct grid positioning
        const startMinutes = moment.tz(draggedWorklog.startTime, userTimezone).hour() * 60 + moment.tz(draggedWorklog.startTime, userTimezone).minute()
        const endMinutes = moment.tz(draggedWorklog.endTime, userTimezone).hour() * 60 + moment.tz(draggedWorklog.endTime, userTimezone).minute()
        const duration = endMinutes - startMinutes

        const slotHeight = getSlotHeight(viewDensity)
        const top = (startMinutes / 15) * slotHeight
        const height = (duration / 15) * slotHeight

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: '4px',
            right: '4px'
        }
    }
    
    // Style calculator for resize preview
    const getResizePreviewStyle = (resizeWorklog) => {
        if (!resizeWorklog) return {}

        // Extract hour/minute in user's timezone for correct grid positioning
        const startMinutes = moment.tz(resizeWorklog.startTime, userTimezone).hour() * 60 + moment.tz(resizeWorklog.startTime, userTimezone).minute()
        const endMinutes = moment.tz(resizeWorklog.endTime, userTimezone).hour() * 60 + moment.tz(resizeWorklog.endTime, userTimezone).minute()
        const duration = endMinutes - startMinutes

        const slotHeight = getSlotHeight(viewDensity)
        const top = (startMinutes / 15) * slotHeight
        const height = (duration / 15) * slotHeight
        
        return {
            top: `${top}px`,
            height: `${height}px`,
            left: '4px',
            right: '4px'
        }
    }

    return {
        // States
        dragStart,
        isDragging,
        dragPreview,
        draggedWorklog,
        isDraggingWorklog,
        dragOffset,
        isAltPressed,
        isShiftPressed,
        
        // Resize states
        isResizing,
        resizeWorklog,
        resizeType,
        
        // Handlers
        handleMouseDown,
        handleWorklogMouseDown,
        handleDoubleClick,
        
        // Style calculators
        getDragPreviewStyle: () => getDragPreviewStyle(dragPreview),
        getDragPreviewStyleForExistingWorklog: () => getDragPreviewStyleForExistingWorklog(draggedWorklog),
        getResizePreviewStyle: () => getResizePreviewStyle(resizeWorklog),
        
        // Handler for starting worklog resize
        handleResizeMouseDown: (e, worklog, type) => {
            e.stopPropagation() // Prevent triggering drag
            setResizeWorklog({ ...worklog })
            setResizeType(type)
            setIsResizing(true)
            setResizeStartData({
                originalStartTime: worklog.startTime,
                originalEndTime: worklog.endTime,
                mouseY: e.clientY
            })
        },
        
        // Utility functions
        resetDragState: () => {
            setDragStart(null)
            setIsDragging(false)
            setDragPreview(null)
            setDraggedWorklog(null)
            setIsDraggingWorklog(false)
            setDragOffset({ x: 0, y: 0 })
            setMouseDownWorklog(null)
            setMouseDownPosition({ x: 0, y: 0 })
            setIsAltPressed(false)
            setIsShiftPressed(false)
            // Reset resize states
            setIsResizing(false)
            setResizeWorklog(null)
            setResizeType(null)
            setResizeStartData(null)
        }
    }
}